import { useEffect, useState, useCallback, useRef } from 'react';

import { TUsePollingQueryParams } from '@/shared/hooks/use-polling-query/use-polling-query.types';

const DEFAULT_PERIODIC_INTERVAL = 2500; // 2.5 second in milliseconds

// By default, a failing request backs the interval off instead of killing
// polling outright, so a transient error (or an API rate limit) can't freeze a
// view for the rest of the session. Polling only gives up after repeated
// failures, and a window focus still refetches from there.
const MAX_CONSECUTIVE_ERRORS = 4;
const MAX_ERROR_BACKOFF_MS = 300_000; // 5 minutes

export const usePollingQuery = <T>({
  useQueryHook,
  queryArg,
  shouldStopPolling = () => false,
  queryOptions = {},
  enableLogging = false,
  pollingInterval = DEFAULT_PERIODIC_INTERVAL,
  stopPollingOnError = false,
}: TUsePollingQueryParams<T>) => {
  const [pollingEnabled, setPollingEnabled] = useState<boolean>(true);
  const [errorCount, setErrorCount] = useState<number>(0);
  const lastHandledError = useRef<unknown>(undefined);

  const getPollingInterval = () => {
    if (
      !pollingEnabled ||
      (stopPollingOnError && errorCount > 0) ||
      errorCount >= MAX_CONSECUTIVE_ERRORS
    ) {
      return 0;
    }

    if (errorCount === 0) {
      return pollingInterval;
    }

    return Math.min(pollingInterval * 2 ** errorCount, MAX_ERROR_BACKOFF_MS);
  };

  const queryResult = useQueryHook(queryArg, {
    // Polling is switched off once nothing is in flight, so focus and reconnect
    // are the only way a change made elsewhere (a pipeline, another tab) reaches
    // an idle view without a remount.
    refetchOnFocus: true,
    refetchOnReconnect: true,
    ...queryOptions,
    pollingInterval: getPollingInterval(),
  });

  const logPollingResults = useCallback(() => {
    if (enableLogging && queryResult?.data) {
      console.debug(`Polling result`, queryResult?.data);
    }
  }, [enableLogging, queryResult?.data]);

  const updatePollingStateBasedOnData = useCallback(() => {
    if (!queryResult?.data) {
      return;
    }

    const shouldStop = shouldStopPolling(queryResult?.data);
    setPollingEnabled(!shouldStop);
  }, [queryResult?.data, shouldStopPolling]);

  const trackErrorState = useCallback(() => {
    const error = queryResult?.error;

    if (!error) {
      lastHandledError.current = undefined;
      setErrorCount(0);
      return;
    }

    // Count attempts, not renders: the same error object can be observed many
    // times before the next request resolves.
    if (lastHandledError.current === error) {
      return;
    }

    lastHandledError.current = error;
    setErrorCount((count) => Math.min(count + 1, MAX_CONSECUTIVE_ERRORS));
  }, [queryResult?.error]);

  useEffect(() => {
    logPollingResults();
  }, [logPollingResults]);

  useEffect(() => {
    updatePollingStateBasedOnData();
  }, [updatePollingStateBasedOnData]);

  useEffect(() => {
    trackErrorState();
  }, [trackErrorState]);

  return {
    ...queryResult,
    pollingEnabled,
    setPollingEnabled,
  };
};
